/**
 * /settings/tokens — Personal Access Tokens ("Applications").
 *
 * A user generates a long-lived API key that authenticates AS them at the BFF.
 * The plaintext token is shown ONCE in a reveal dialog; the list thereafter
 * shows only the non-secret prefix. Revoking a token stops it immediately.
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, Loader2, Copy, Trash2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { ErrorState } from "@/components/ErrorState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  useApplications,
  useCreateApplication,
  useRevokeApplication,
} from "@/lib/api/applications.queries";
import type { Application } from "@/lib/api/applications";

export const Route = createFileRoute("/settings/tokens")({
  component: TokensPage,
});

function TokensPage() {
  return (
    <RequireAuth>
      <AppShell>
        <TokensContent />
      </AppShell>
    </RequireAuth>
  );
}

/** SQLite `datetime('now')` is UTC without a tz marker — normalise for display. */
function fmtDate(s: string | null, fallback: string): string {
  if (!s) return fallback;
  const iso = s.includes("T") ? s : `${s.replace(" ", "T")}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
}

function TokensContent() {
  const { t } = useTranslation();
  const query = useApplications();

  const [createOpen, setCreateOpen] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Application | null>(null);

  const apps = query.data?.applications ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("applications.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("applications.subtitle")}
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("applications.create_cta")}
        </Button>
      </div>

      {query.isPending ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : query.isError ? (
        <ErrorState
          title={t("applications.error.load_title")}
          description={t("applications.error.load_body")}
          onRetry={() => void query.refetch()}
          retryLabel={t("common.retry")}
          error={query.error}
        />
      ) : apps.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <KeyRound className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="font-medium">{t("applications.empty.title")}</p>
              <p className="text-sm text-muted-foreground">
                {t("applications.empty.body")}
              </p>
            </div>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t("applications.create_cta")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("applications.table.name")}</TableHead>
                  <TableHead>{t("applications.table.prefix")}</TableHead>
                  <TableHead className="hidden sm:table-cell">
                    {t("applications.table.created")}
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    {t("applications.table.lastUsed")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("applications.table.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell className="font-medium">{app.name}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {app.keyPrefix}…
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                      {fmtDate(app.createdAt, "—")}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                      {fmtDate(app.lastUsedAt, t("applications.never"))}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => setRevokeTarget(app)}
                      >
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                        {t("applications.revoke.action")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CreateTokenDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(token) => setNewToken(token)}
      />
      <TokenRevealDialog token={newToken} onClose={() => setNewToken(null)} />
      <RevokeConfirmDialog
        target={revokeTarget}
        onClose={() => setRevokeTarget(null)}
      />
    </div>
  );
}

// ── Create dialog ─────────────────────────────────────────────────────────────

function CreateTokenDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (token: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateApplication();
  const [name, setName] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const res = await create.mutateAsync({ name: trimmed });
      onOpenChange(false);
      setName("");
      onCreated(res.apiKey);
      toast.success(t("applications.toast.created"));
    } catch {
      toast.error(t("applications.toast.create_failed"));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setName("");
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("applications.create.title")}</DialogTitle>
          <DialogDescription>
            {t("applications.create.body")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="token-name">
              {t("applications.form.name_label")}
            </Label>
            <Input
              id="token-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("applications.form.name_placeholder")}
              maxLength={100}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              {create.isPending && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              {t("applications.form.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Reveal dialog (the secret, shown exactly once) ────────────────────────────

function TokenRevealDialog({
  token,
  onClose,
}: {
  token: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={!!token} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("applications.reveal.title")}</DialogTitle>
          <DialogDescription>
            {t("applications.reveal.warn_once")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={token ?? ""}
            className="font-mono text-xs"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t("applications.reveal.copy")}
            onClick={async () => {
              if (!token) return;
              try {
                await navigator.clipboard.writeText(token);
                toast.success(t("applications.toast.copied"));
              } catch {
                toast.error(t("applications.toast.copy_failed"));
              }
            }}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>{t("applications.reveal.done")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Revoke confirmation ───────────────────────────────────────────────────────

function RevokeConfirmDialog({
  target,
  onClose,
}: {
  target: Application | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const revoke = useRevokeApplication();

  async function handleConfirm() {
    if (!target) return;
    try {
      await revoke.mutateAsync(target.id);
      onClose();
      toast.success(t("applications.toast.revoked"));
    } catch {
      toast.error(t("applications.toast.revoke_failed"));
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("applications.revoke.title")}</DialogTitle>
          <DialogDescription>
            {t("applications.revoke.body", { name: target?.name ?? "" })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={revoke.isPending}
            onClick={handleConfirm}
          >
            {revoke.isPending && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            {t("applications.revoke.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
